import Pagination from '@mui/material/Pagination';
import SellerTable from 'components/commonTable/SellerTable';
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SellerDto } from 'services/data-contracts';
import ServiceHelper from 'stores/ServiceHelper';
import { useStoreWrapper } from 'stores/StoreHelper';
import styled from 'styled-components';
import { usePaginationHeader, PaginationInfo } from 'hooks/usePagenation';
import { SyncLoader } from 'react-spinners';
import { Row } from 'saladsoft-primitive';
import { useTranslation } from 'react-i18next';
import { SearchParams } from 'types/CommonTypes';
import { SortingState } from '@tanstack/react-table';
import BusinessInfoCleansingModal from 'containers/BusinessInfoCleansingModal';
import SellerFilterBox from 'components/commons/SellerFilterBox';

const BusinessInfoClensingPage = () => {
  const tableRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const { uiStore } = useStoreWrapper();

  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [sellerInfos, setSellerInfos] = useState<SellerDto[]>([]);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([]);

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);

    // 현재 검색 파라미터를 유지하면서 페이지만 업데이트
    const currentParams: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      currentParams[key] = value;
    });

    setSearchParams({
      ...currentParams,
      page: String(value),
    });
  };

  const handleSearch = (searchParams: SearchParams) => {
    const params = Object.entries({
      size: String(searchParams.size),
      page: String(searchParams.page),

      searchKey: searchParams.searchKey ?? '',
      searchValue: searchParams.searchValue ?? '',
      sort: searchParams.sort ?? '',
    }).reduce(
      (acc, [key, value]) => {
        if (value !== 'undefined' && value !== '') {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, string>,
    );

    setSearchParams(params);
  };

  const handleRowClick = (row: SellerDto) => {
    uiStore?.modal.show({
      style: {
        width: '70%',
        height: '100%',
        maxHeight: '70%',
      },
      title: t('seller.businessInfoCleansing'),
      children: <BusinessInfoCleansingModal row={row} />,
    });
  };

  const handleSortingChange = (newSorting: SortingState) => {
    setSorting(newSorting);

    if (newSorting.length > 0) {
      const sortId = newSorting[0].id;
      const sortOrder = newSorting[0].desc ? 'desc' : 'asc';
      const sortParam = `${sortId},${sortOrder}`;

      // 현재 검색 파라미터에 정렬 정보 추가
      const currentParams: Record<string, string> = {};
      searchParams.forEach((value, key) => {
        currentParams[key] = value;
      });

      setSearchParams({
        ...currentParams,
        sort: sortParam,
      });
    } else {
      // 정렬이 없는 경우 정렬 파라미터 제거
      const currentParams: Record<string, string> = {};
      searchParams.forEach((value, key) => {
        currentParams[key] = value;
      });
      const { sort, order, ...restParams } = currentParams;
      setSearchParams(restParams);
    }
  };

  const fetchSellerInfo = (page?: number, searchParams?: SearchParams) => {
    setLoading(true);
    const searchValue = searchParams?.searchValue?.trim() ?? '';
    ServiceHelper?.sellerService
      ?.getSellers({
        isUnMatch: true,
        page: typeof page === 'number' ? page - 1 : 0,
        size: searchParams?.size,
        searchKeyword: searchValue,
        sort: searchParams?.sort ? [searchParams.sort] : undefined,
      })
      .then((res) => {
        if (res.status === 200) {
          setSellerInfos(res.data);
          setPaginationInfo(usePaginationHeader(res.headers));
        }
      })
      .catch((err) => {
        alert();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    ServiceHelper.reset();
    const size = searchParams.get('size');
    const page = searchParams.get('page');
    const searchKey = searchParams.get('searchKey');
    const searchValue = searchParams.get('searchValue');
    const sort = searchParams.get('sort');

    if (page) {
      setPage(parseInt(page));
    }

    fetchSellerInfo(page ? parseInt(page) : 1, {
      size: size ? parseInt(size) : 50,
      page: page ? parseInt(page) : 1,
      searchKey: searchKey ?? '',
      searchValue: searchValue ?? '',
      sort: sort ?? '',
    });
  }, [location.search]);

  useEffect(() => {
    if (sellerInfos.length > 0) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [loading]);

  return (
    <>
      <Row justifyContent="space-between" padding="0 20px">
        <SellerFilterBox onSearch={handleSearch} />
        <Row gap={5}>
          <RecordText>{paginationInfo?.totalCount?.toLocaleString()}</RecordText>
          <div>{t('count')}</div>
        </Row>
      </Row>
      {!loading && sellerInfos.length === 0 ? (
        <NoContentStyle>
          <div>{t('No data found')}</div>
        </NoContentStyle>
      ) : (
        <TableWrapper ref={tableRef}>
          <SellerTable
            handleRowClick={handleRowClick}
            data={sellerInfos}
            onSortingChange={handleSortingChange}
            externalSorting={sorting}
          />

          <PaginationWrapper>
            <PaginationWrapperInner>
              <Row gap={20}>
                <Pagination
                  color="primary"
                  count={paginationInfo?.totalPages}
                  page={page || 1}
                  onChange={handleChange}
                />
              </Row>
            </PaginationWrapperInner>
          </PaginationWrapper>
        </TableWrapper>
      )}

      <OverlayStyle show={loading}>
        <SyncLoader color="#930000" size={10} />
      </OverlayStyle>
    </>
  );
};

const TableWrapper = styled.div`
  width: 100%;
  min-height: calc(100vh - 200px);
  padding: 10px 20px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  position: relative;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 50px;
  position: fixed;
  bottom: 10px;

  z-index: 2;

  left: 0;
  right: 0;
  margin: auto;
`;

const PaginationWrapperInner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: #fff;
  border-radius: 20px;
  padding: 10px;
`;

const OverlayStyle = styled.div<{ show: boolean }>`
  position: fixed;

  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: ${(props) => (props.show ? 1 : 0)};
  transition: opacity 0.1s ease-in-out;
  pointer-events: ${(props) => (props.show ? 'auto' : 'none')};
`;

const RecordText = styled.div`
  color: #6e00c7;
  text-align: center;
  font-family: Ubuntu;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 21px; /* 131.25% */
`;

const NoContentStyle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 200px);
  > div {
    padding: 100px;
    border: 1px solid #000;
    border-radius: 10px;
  }
`;

export default BusinessInfoClensingPage;
